import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormHelperText,
    InputLabel,
    OutlinedInput,
    Typography,
} from '@mui/material';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';

import CustomModal from 'components/customModal';
import AnimateButton from 'components/animateButton';
import { BRAND } from 'themes';
import { getBusinessDetail, updateBusinessDetail } from './action';

const HelpSupportSchema = Yup.object().shape({
    businessContact: Yup.string()
        .trim()
        .required('Enter business contact')
        .matches(/^\+?\d{8,15}$/, 'Enter a valid contact number'),
    businessEmail: Yup.string()
        .trim()
        .required('Enter business email')
        .email('Please enter a valid email address'),
});

type HelpSupportModalProps = {
    open: boolean;
    onClose: () => void;
};

const HelpSupportModal = ({ open, onClose }: HelpSupportModalProps) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [initialValues, setInitialValues] = useState({
        businessContact: '',
        businessEmail: '',
    });

    useEffect(() => {
        if (!open) return;

        let active = true;
        setLoading(true);
        getBusinessDetail((data) => {
            if (!active) return;
            if (data) {
                setInitialValues({
                    businessContact: data.businessContact,
                    businessEmail: data.businessEmail,
                });
            }
            setLoading(false);
        });

        return () => {
            active = false;
        };
    }, [open]);

    const handleClose = () => {
        if (saving) return;
        onClose();
    };

    return (
        <CustomModal
            title='Help & Support'
            visible={open}
            onClose={handleClose}
            maxWidth={460}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    mb: 2.5,
                    p: 1.5,
                    borderRadius: '12px',
                    bgcolor: BRAND.track,
                }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: '#fff',
                        color: BRAND.primary,
                        flexShrink: 0,
                    }}>
                    <SupportAgentOutlinedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: 14,
                            lineHeight: 1.3,
                            color: BRAND.ink,
                        }}>
                        Business contact details
                    </Typography>
                    <Typography
                        sx={{
                            mt: 0.5,
                            fontSize: 13,
                            lineHeight: 1.4,
                            color: '#5C5870',
                        }}>
                        Update the phone and email shown for help & support.
                    </Typography>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
                    <CircularProgress size={32} sx={{ color: BRAND.primary }} />
                </Box>
            ) : (
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={HelpSupportSchema}
                    onSubmit={(values) => {
                        setSaving(true);
                        updateBusinessDetail(
                            {
                                businessContact: values.businessContact.trim(),
                                businessEmail: values.businessEmail.trim(),
                            },
                            (data) => {
                                setSaving(false);
                                if (data) {
                                    setInitialValues({
                                        businessContact: data.businessContact,
                                        businessEmail: data.businessEmail,
                                    });
                                    onClose();
                                }
                            }
                        );
                    }}>
                    {({
                        values,
                        errors,
                        touched,
                        handleBlur,
                        handleChange,
                        handleSubmit,
                    }) => (
                        <Box
                            component='form'
                            noValidate
                            onSubmit={handleSubmit}
                            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <FormControl
                                fullWidth
                                error={Boolean(touched.businessContact && errors.businessContact)}>
                                <InputLabel htmlFor='help-business-contact'>
                                    Business contact
                                </InputLabel>
                                <OutlinedInput
                                    id='help-business-contact'
                                    name='businessContact'
                                    label='Business contact'
                                    value={values.businessContact}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    disabled={saving}
                                />
                                {touched.businessContact && errors.businessContact ? (
                                    <FormHelperText error>
                                        {errors.businessContact}
                                    </FormHelperText>
                                ) : null}
                            </FormControl>

                            <FormControl
                                fullWidth
                                error={Boolean(touched.businessEmail && errors.businessEmail)}>
                                <InputLabel htmlFor='help-business-email'>
                                    Business email
                                </InputLabel>
                                <OutlinedInput
                                    id='help-business-email'
                                    name='businessEmail'
                                    type='email'
                                    label='Business email'
                                    value={values.businessEmail}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    disabled={saving}
                                />
                                {touched.businessEmail && errors.businessEmail ? (
                                    <FormHelperText error>{errors.businessEmail}</FormHelperText>
                                ) : null}
                            </FormControl>

                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: 1.25,
                                    mt: 1,
                                }}>
                                <Button
                                    disableElevation
                                    size='large'
                                    type='button'
                                    variant='outlined'
                                    color='secondary'
                                    disabled={saving}
                                    onClick={handleClose}>
                                    Cancel
                                </Button>
                                <AnimateButton>
                                    <Button
                                        disableElevation
                                        size='large'
                                        type='submit'
                                        variant='contained'
                                        color='secondary'
                                        disabled={saving}
                                        startIcon={
                                            saving ? (
                                                <CircularProgress size={16} color='inherit' />
                                            ) : undefined
                                        }>
                                        {saving ? 'Saving…' : 'Save'}
                                    </Button>
                                </AnimateButton>
                            </Box>
                        </Box>
                    )}
                </Formik>
            )}
        </CustomModal>
    );
};

export default HelpSupportModal;
